import * as THREE from 'three';
import { CONFIG } from '../config.js';
import { Tree } from '../entities/Tree.js';
import { House } from '../entities/House.js';
import { Cow } from '../entities/Cow.js';
import { Cloud } from './Cloud.js';

/**
 * Generates and places trees, houses, cows, and clouds in the world.
 * Ensures minimum spacing between objects.
 */
export class WorldGenerator {
    /** @type {Tree[]} */
    #trees = [];

    /** @type {House[]} */
    #houses = [];

    /** @type {Cow[]} */
    #cows = [];

    /** @type {Cloud[]} */
    #clouds = [];

    /**
     * Check whether a position is inside (or too close to) any house footprint.
     * Uses circular approximation for house footprint.
     * @param {number} x
     * @param {number} z
     * @param {number} [padding]
     * @returns {boolean}
     */
    #isInsideHouseFootprint(x, z, padding = 0) {
        for (const house of this.#houses) {
            const dx = house.position.x - x;
            const dz = house.position.z - z;
            const radius = House.COLLISION_RADIUS + padding;
            if ((dx * dx) + (dz * dz) < radius * radius) {
                return true;
            }
        }

        return false;
    }

    /**
     * Check whether a position is too close to the cat spawn point (world origin).
     * @param {number} x
     * @param {number} z
     * @param {number} clearance
     * @returns {boolean}
     */
    #isNearCatSpawn(x, z, clearance) {
        return (x * x) + (z * z) < clearance * clearance;
    }

    /**
     * Generate the world
     * @param {THREE.Scene} scene - The scene to add objects to
     * @param {THREE.Texture} poopTexture - Poop emoji texture for houses
     */
    generate(scene, poopTexture) {
        this.#generateTrees(scene);
        this.#generateHouses(scene, poopTexture);
        this.#generateCows(scene);
        this.#generateClouds(scene);
    }

    /**
     * Get all trees
     * @returns {Tree[]}
     */
    get trees() {
        return this.#trees;
    }

    /**
     * Get all houses
     * @returns {House[]}
     */
    get houses() {
        return this.#houses;
    }

    /**
     * Get all cows
     * @returns {Cow[]}
     */
    get cows() {
        return this.#cows;
    }

    /**
     * Get all clouds
     * @returns {Cloud[]}
     */
    get clouds() {
        return this.#clouds;
    }

    /**
     * Get all collidable objects (trees, houses, cows)
     * @returns {(Tree|House|Cow)[]}
     */
    getCollidableObjects() {
        const staticCollidables = [...this.#trees, ...this.#houses, ...this.#cows]
            .filter(entity => entity.collidable !== false);

        const debrisCollidables = this.#houses.flatMap(house =>
            typeof house.getDebrisObstacles === 'function' ? house.getDebrisObstacles() : []
        );

        return [...staticCollidables, ...debrisCollidables];
    }

    /**
     * Check if a position is too close to existing objects
     * @param {number} x - X coordinate
     * @param {number} z - Z coordinate
     * @param {number} minDistance - Minimum allowed distance
     * @returns {boolean}
     */
    #isTooClose(x, z, minDistance) {
        // Check trees
        for (const tree of this.#trees) {
            const dx = tree.position.x - x;
            const dz = tree.position.z - z;
            if (dx * dx + dz * dz < minDistance * minDistance) return true;
        }

        // Check houses
        for (const house of this.#houses) {
            const dx = house.position.x - x;
            const dz = house.position.z - z;
            if (dx * dx + dz * dz < minDistance * minDistance) return true;
        }

        // Check cows
        for (const cow of this.#cows) {
            const dx = cow.position.x - x;
            const dz = cow.position.z - z;
            if (dx * dx + dz * dz < minDistance * minDistance) return true;
        }

        return false;
    }

    /**
     * Generate trees
     * @param {THREE.Scene} scene
     */
    #generateTrees(scene) {
        for (let i = 0; i < CONFIG.world.treeCount; i++) {
            let x, z;
            do {
                x = (Math.random() - 0.5) * 190;
                z = (Math.random() - 0.5) * 190;
            } while (this.#isTooClose(x, z, CONFIG.world.minObjectDistance));

            const tree = new Tree({
                position: new THREE.Vector3(x, 0, z)
            });
            tree.onAdd(scene);
            this.#trees.push(tree);
        }
    }

    /**
     * Generate houses
     * @param {THREE.Scene} scene
     * @param {THREE.Texture} poopTexture
     */
    #generateHouses(scene, poopTexture) {
        for (let i = 0; i < CONFIG.world.houseCount; i++) {
            let x, z, rotation;
            let attempts = 0;
            const maxAttempts = 120;

            do {
                x = (Math.random() - 0.5) * 170;
                z = (Math.random() - 0.5) * 170;
                rotation = Math.random() * Math.PI * 2;
                attempts++;
            } while (
                attempts < maxAttempts && (
                    this.#isTooClose(x, z, CONFIG.world.minHouseDistance)
                    || this.#isNearCatSpawn(x, z, House.COLLISION_RADIUS + CONFIG.cat.collisionRadius + 1.5)
                )
            );

            if (attempts >= maxAttempts) {
                continue;
            }

            const house = new House({
                position: new THREE.Vector3(x, 0, z),
                rotation: rotation
            });
            house.onAdd(scene);
            this.#houses.push(house);
        }
    }

    /**
     * Generate cows
     * @param {THREE.Scene} scene
     */
    #generateCows(scene) {
        for (let i = 0; i < CONFIG.cow.count; i++) {
            let x, z;
            let attempts = 0;
            const maxAttempts = 50;

            do {
                x = (Math.random() - 0.5) * 180;
                z = (Math.random() - 0.5) * 180;
                attempts++;
            } while (
                attempts < maxAttempts
                && (
                    this.#isTooClose(x, z, 5)
                    || this.#isInsideHouseFootprint(x, z, CONFIG.cow.collisionRadius + 0.6)
                )
            );

            if (attempts >= maxAttempts) continue;

            const cow = new Cow({
                position: new THREE.Vector3(x, 0, z),
                rotation: Math.random() * Math.PI * 2
            });
            cow.onAdd(scene);
            this.#cows.push(cow);
        }
    }

    /**
     * Generate clouds
     * @param {THREE.Scene} scene
     */
    #generateClouds(scene) {
        for (let i = 0; i < CONFIG.world.cloudCount; i++) {
            const posX = THREE.MathUtils.randFloatSpread(400);
            const posY = THREE.MathUtils.randFloat(CONFIG.cloud.heightMin, CONFIG.cloud.heightMax);
            const posZ = THREE.MathUtils.randFloatSpread(400);

            const cloud = new Cloud({
                position: new THREE.Vector3(posX, posY, posZ)
            });
            cloud.onAdd(scene);
            this.#clouds.push(cloud);
        }
    }

    /**
     * Update all dynamic world objects
     * @param {number} delta - Time since last frame in seconds
     */
    update(delta) {
        // Update clouds (drift)
        this.#clouds.forEach(cloud => cloud.update(delta));

        // Update trees (burn progression)
        this.#trees.forEach(tree => tree.update(delta));

        // Update houses (burn progression + debris physics)
        this.#houses.forEach(house => house.update(delta, this.#houses, this.#trees));

        // Update cows (wandering AI)
        const collidables = this.getCollidableObjects();
        this.#cows.forEach(cow => {
            cow.update(delta, CONFIG.world.boundary, collidables);
        });
    }

    /**
     * Dispose of all world objects
     */
    dispose() {
        [...this.#trees, ...this.#houses, ...this.#cows, ...this.#clouds].forEach(obj => obj.dispose());
        this.#trees = [];
        this.#houses = [];
        this.#cows = [];
        this.#clouds = [];
    }
}
